(() => {
  const initialized = new WeakSet();
  const tuneDurationMs = 19300;
  const beatSeconds = 60 / 104;

  const frequencies = {
    F2: 87.31,
    G2: 98,
    A2: 110,
    Bb2: 116.54,
    C3: 130.81,
    D3: 146.83,
    E3: 164.81,
    F3: 174.61,
    G3: 196,
    A3: 220,
    Bb3: 233.08,
    C4: 261.63,
    D4: 293.66,
    E4: 329.63,
    F4: 349.23,
    G4: 392,
    A4: 440,
    Bb4: 466.16,
    C5: 523.25,
    D5: 587.33,
    E5: 659.25,
    F5: 698.46,
  };

  const melodyBars = [
    [['F4', 0.5], ['A4', 0.5], ['C5', 1], ['A4', 0.5], ['G4', 0.5], ['F4', 1]],
    [['Bb4', 0.5], ['D5', 0.5], ['F5', 1], ['D5', 0.5], ['C5', 0.5], ['Bb4', 1]],
    [['A4', 1], ['C5', 0.5], ['D5', 0.5], ['C5', 1], ['A4', 1]],
    [['G4', 0.5], ['A4', 0.5], ['C5', 1], ['E5', 0.5], ['D5', 0.5], ['C5', 1]],
    [['F4', 0.5], ['A4', 0.5], ['D5', 1], ['C5', 0.5], ['A4', 0.5], ['F4', 1]],
    [['G4', 0.5], ['Bb4', 0.5], ['D5', 1], ['C5', 0.5], ['Bb4', 0.5], ['A4', 1]],
    [['A4', 0.5], ['C5', 0.5], ['F5', 1], ['E5', 0.5], ['D5', 0.5], ['C5', 1]],
    [['G4', 0.5], ['A4', 0.5], ['C5', 1], ['A4', 1], ['F4', 1]],
  ];

  const chordBars = [
    ['F3', 'A3', 'D4'],
    ['Bb2', 'D3', 'G3'],
    ['A2', 'C3', 'F3'],
    ['C3', 'E3', 'Bb3'],
    ['D3', 'F3', 'A3'],
    ['G2', 'Bb2', 'D3'],
    ['F3', 'A3', 'D4'],
    ['F3', 'A3', 'C4'],
  ];

  const bassBars = [
    ['F2', 'C3'],
    ['Bb2', 'F2'],
    ['A2', 'C3'],
    ['C3', 'G2'],
    ['D3', 'A2'],
    ['G2', 'C3'],
    ['F2', 'C3'],
    ['F2', 'C3'],
  ];

  function initialize(root) {
    if (!root || initialized.has(root)) return;
    initialized.add(root);
    initializeSlider(root);
    initializeTune(root);
  }

  function initializeSlider(root) {
    const tabs = Array.from(root.querySelectorAll('[data-ds-tab]'));
    const panels = Array.from(root.querySelectorAll('[data-ds-panel]'));
    if (!tabs.length || !panels.length) return;

    tabs.forEach((tab) => {
      tab.addEventListener('click', () => {
        const selected = tab.getAttribute('data-ds-tab');
        tabs.forEach((candidate) => {
          const active = candidate === tab;
          candidate.setAttribute('aria-selected', String(active));
          candidate.tabIndex = active ? 0 : -1;
        });
        panels.forEach((panel) => {
          panel.hidden = panel.getAttribute('data-ds-panel') !== selected;
        });
      });

      tab.addEventListener('keydown', (event) => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        const current = tabs.indexOf(tab);
        let next = current;
        if (event.key === 'ArrowRight') next = (current + 1) % tabs.length;
        if (event.key === 'ArrowLeft') next = (current - 1 + tabs.length) % tabs.length;
        if (event.key === 'Home') next = 0;
        if (event.key === 'End') next = tabs.length - 1;
        tabs[next].focus();
        tabs[next].click();
      });
    });
  }

  function initializeTune(root) {
    const button = root.querySelector('[data-ds-tune]');
    if (!button) return;

    const label = button.querySelector('[data-ds-tune-label]');
    const icon = button.querySelector('[data-ds-tune-icon]');
    const status = root.querySelector('[data-ds-tune-status]');
    let activeSession = null;
    let finishTimer = null;

    const setPlaying = (playing) => {
      button.setAttribute('aria-pressed', String(playing));
      button.dataset.playing = String(playing);
      if (label) label.textContent = playing ? 'Stop story tune' : 'Play story tune';
      if (icon) icon.textContent = playing ? '■' : '▶';
    };

    const stop = async () => {
      if (finishTimer) window.clearTimeout(finishTimer);
      finishTimer = null;
      const session = activeSession;
      activeSession = null;
      setPlaying(false);
      if (session) await session.stop();
    };

    button.addEventListener('click', async () => {
      if (activeSession) {
        await stop();
        return;
      }

      if (status) status.textContent = '';
      button.disabled = true;
      try {
        activeSession = await startTune();
        setPlaying(true);
        finishTimer = window.setTimeout(() => {
          void stop();
        }, tuneDurationMs);
      } catch (_error) {
        if (status) status.textContent = 'The music preview is not available in this browser.';
        setPlaying(false);
      } finally {
        button.disabled = false;
      }
    });

    root.dsStopTune = stop;
  }

  async function startTune() {
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextClass) throw new Error('Web Audio unavailable');

    const context = new AudioContextClass();
    await context.resume();

    const compressor = context.createDynamicsCompressor();
    compressor.threshold.value = -18;
    compressor.knee.value = 16;
    compressor.ratio.value = 4;
    compressor.attack.value = 0.01;
    compressor.release.value = 0.2;

    const master = context.createGain();
    master.gain.setValueAtTime(0.0001, context.currentTime);
    master.gain.linearRampToValueAtTime(0.14, context.currentTime + 0.08);
    compressor.connect(master);
    master.connect(context.destination);

    const scoreStart = context.currentTime + 0.05;
    scheduleMelody(context, compressor, scoreStart);
    scheduleBass(context, compressor, scoreStart);
    scheduleChords(context, compressor, scoreStart);
    scheduleDrums(context, compressor, scoreStart);

    let stopped = false;
    return {
      stop: async () => {
        if (stopped) return;
        stopped = true;
        const now = context.currentTime;
        master.gain.cancelScheduledValues(now);
        master.gain.setValueAtTime(Math.max(master.gain.value, 0.0001), now);
        master.gain.exponentialRampToValueAtTime(0.0001, now + 0.08);
        await new Promise((resolve) => window.setTimeout(resolve, 100));
        if (context.state !== 'closed') await context.close();
      },
    };
  }

  function scheduleMelody(context, destination, scoreStart) {
    melodyBars.forEach((bar, barIndex) => {
      let cursor = barIndex * 4;
      bar.forEach(([note, durationBeats]) => {
        const start = scoreStart + swungBeat(cursor) * beatSeconds;
        const duration = Math.max(durationBeats * beatSeconds - 0.18, 0.12);
        scheduleOscillator(context, destination, frequencies[note], start, duration, 'triangle', 0.24);
        scheduleOscillator(context, destination, frequencies[note] * 2, start, duration * 0.9, 'sine', 0.035);
        cursor += durationBeats;
      });
    });
  }

  function scheduleBass(context, destination, scoreStart) {
    bassBars.forEach(([root, fifth], barIndex) => {
      for (let beat = 0; beat < 4; beat += 1) {
        const note = beat % 2 === 0 ? root : fifth;
        scheduleOscillator(
          context,
          destination,
          frequencies[note],
          scoreStart + (barIndex * 4 + beat) * beatSeconds,
          beatSeconds * 0.72,
          'sine',
          0.3,
        );
      }
    });
  }

  function scheduleChords(context, destination, scoreStart) {
    chordBars.forEach((chord, barIndex) => {
      for (let beat = 0; beat < 4; beat += 1) {
        const start = scoreStart + (barIndex * 4 + beat + 0.6) * beatSeconds;
        chord.forEach((note) => {
          scheduleOscillator(context, destination, frequencies[note], start, beatSeconds * 0.22, 'triangle', 0.08);
        });
      }
    });
  }

  function scheduleDrums(context, destination, scoreStart) {
    for (let bar = 0; bar < 8; bar += 1) {
      [0, 2].forEach((beat) => scheduleKick(context, destination, scoreStart + (bar * 4 + beat) * beatSeconds));
      [1, 3].forEach((beat) => {
        scheduleSnare(context, destination, scoreStart + (bar * 4 + beat) * beatSeconds, bar * 8 + beat);
      });
    }
  }

  function scheduleOscillator(context, destination, frequency, start, duration, type, volume) {
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    oscillator.type = type;
    oscillator.frequency.setValueAtTime(frequency, start);
    gain.gain.setValueAtTime(0.0001, start);
    gain.gain.exponentialRampToValueAtTime(volume, start + 0.018);
    gain.gain.setValueAtTime(volume * 0.72, start + Math.max(duration - 0.09, 0.03));
    gain.gain.exponentialRampToValueAtTime(0.0001, start + duration);
    oscillator.connect(gain);
    gain.connect(destination);
    oscillator.start(start);
    oscillator.stop(start + duration + 0.02);
  }

  function scheduleKick(context, destination, start) {
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    oscillator.type = 'sine';
    oscillator.frequency.setValueAtTime(115, start);
    oscillator.frequency.exponentialRampToValueAtTime(52, start + 0.13);
    gain.gain.setValueAtTime(0.28, start);
    gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.16);
    oscillator.connect(gain);
    gain.connect(destination);
    oscillator.start(start);
    oscillator.stop(start + 0.17);
  }

  function scheduleSnare(context, destination, start, seed) {
    const sampleCount = Math.floor(context.sampleRate * 0.11);
    const buffer = context.createBuffer(1, sampleCount, context.sampleRate);
    const samples = buffer.getChannelData(0);
    let state = seed + 1;
    for (let index = 0; index < sampleCount; index += 1) {
      state = (state * 16807) % 2147483647;
      samples[index] = (state / 2147483647) * 2 - 1;
    }
    const source = context.createBufferSource();
    const filter = context.createBiquadFilter();
    const gain = context.createGain();
    source.buffer = buffer;
    filter.type = 'highpass';
    filter.frequency.value = 1250;
    gain.gain.setValueAtTime(0.12, start);
    gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.11);
    source.connect(filter);
    filter.connect(gain);
    gain.connect(destination);
    source.start(start);
  }

  function swungBeat(beat) {
    const wholeBeat = Math.floor(beat);
    return beat - wholeBeat === 0.5 ? wholeBeat + 0.6 : beat;
  }

  function initializeAll(scope = document) {
    scope.querySelectorAll('[data-ds-home]').forEach(initialize);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeAll());
  } else {
    initializeAll();
  }

  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target.querySelector('[data-ds-home]');
    if (root && typeof root.dsStopTune === 'function') void root.dsStopTune();
  });
})();
